// src/components/ForegroundPushBanner.jsx
// Shows a visible in-app banner when a push arrives while the app is open.
// The native OS popup may be suppressed on some Android/PWA configs when the
// app is focused — this banner ensures the user always sees the notification.
import { useState, useEffect, useCallback, useRef } from 'react'
import { playNotificationSound } from '../lib/notificationSound'
import { useNavigate } from 'react-router-dom'

export default function ForegroundPushBanner() {
  const [banner, setBanner] = useState(null) // { title, body, url }
  const timerRef = useRef(null)
  const navigate = useNavigate()

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current)
    setBanner(null)
  }, [])

  const open = useCallback((url) => {
    dismiss()
    if (url && url !== '/') {
      navigate(url.replace(/^https?:\/\/[^/]+/, ''))
    }
  }, [dismiss, navigate])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const handler = (event) => {
      if (event.data?.type !== 'BELLASON_PUSH_RECEIVED') return
      const { title, body, url } = event.data
      playNotificationSound(false)
      setBanner({ title: title || 'Bellason', body: body || '', url: url || '/' })
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(dismiss, 5000)
    }

    navigator.serviceWorker.addEventListener('message', handler)
    return () => navigator.serviceWorker.removeEventListener('message', handler)
  }, [dismiss])

  if (!banner) return null

  return (
    <div
      role="alert"
      onClick={() => open(banner.url)}
      style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: 'min(92vw, 380px)',
        background: 'var(--bln-surface, #1e1e2e)',
        border: '1px solid var(--bln-accent, #a78bfa)',
        borderRadius: '14px',
        padding: '12px 14px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        cursor: 'pointer',
        animation: 'bln-slide-in 0.25s ease',
      }}
    >
      <img src="/icon-192.png" alt="" style={{ width: 38, height: 38, borderRadius: 8, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: 'var(--bln-text, #fff)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {banner.title}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--bln-text-muted, #aaa)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {banner.body}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); dismiss() }}
        style={{ background: 'none', border: 'none', color: 'var(--bln-text-muted, #aaa)', fontSize: '1.1rem', cursor: 'pointer', padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  )
}
